import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Stack, Avatar, Divider, Button, Chip
} from "@mui/material";
import {
  Lightbulb, Handshake, TrendingUp, Shield, Groups, EmojiObjects,
  Verified, ArrowForward, Public, Rocket, FavoriteBorder, Code
} from "@mui/icons-material";
import { Link } from "react-router-dom";

const accent = "#0d47a1";
const accentLight = "#e3f2fd";
const accentDark = "#082e6a";

const values = [
  {
    icon: <FavoriteBorder sx={{ fontSize: 32 }} />,
    title: "Client Ownership",
    desc: "We believe every business should own its customers and revenue — not rent them from a marketplace at 30% commission.",
  },
  {
    icon: <EmojiObjects sx={{ fontSize: 32 }} />,
    title: "Innovation First",
    desc: "From AI voice agents to automated marketing, we integrate cutting-edge technology that gives small businesses enterprise-level tools.",
  },
  {
    icon: <Handshake sx={{ fontSize: 32 }} />,
    title: "Partnership, Not Dependency",
    desc: "We coexist with marketplaces while building your direct channel — no forced migration, just gradual, profitable independence.",
  },
  {
    icon: <Shield sx={{ fontSize: 32 }} />,
    title: "Security & Compliance",
    desc: "GDPR, HIPAA, and PCI-DSS compliant from day one. Your data is encrypted, protected, and always yours to export.",
  },
  {
    icon: <Public sx={{ fontSize: 32 }} />,
    title: "Extensible by Design",
    desc: "Built on a category-driven architecture — today it's restaurants and healthcare; tomorrow, retail, salons, gyms, and beyond.",
  },
  {
    icon: <Code sx={{ fontSize: 32 }} />,
    title: "White-Label Everything",
    desc: "Your brand on every touchpoint — website, app, emails, voice agent greeting, and even the POS terminal.",
  },
];

const milestones = [
  { year: "2023", title: "Idea Born", desc: "Recognised the 15-30% commission burden on restaurants and clinics from marketplace platforms." },
  { year: "2024", title: "Platform Design", desc: "Architected a 16-module, category-driven SaaS platform with AI-first capabilities." },
  { year: "2024", title: "V2 Scope Finalised", desc: "Incorporated POS hardware, driver app, multi-gateway payments, and AI voice agent into the core." },
  { year: "2025", title: "MVP Launch", desc: "Phase 1 delivery with onboarding, payments, POS, ordering, booking, and branded websites." },
  { year: "2025", title: "Phase 2 Expansion", desc: "Loyalty, marketing automation, driver app, CRM analytics, and customer acquisition tools." },
  { year: "2026", title: "AI Voice Agent", desc: "ElevenLabs + ChatGPT powered 24/7 voice agent for orders, bookings, and customer support." },
];

const teamExpertise = [
  { area: "Frontend Development", desc: "Modern React-based responsive interfaces with brand-first design systems." },
  { area: "Backend Architecture", desc: "Scalable Node.js microservices with multi-tenant SaaS architecture." },
  { area: "AI & Machine Learning", desc: "ElevenLabs voice synthesis, ChatGPT NLU, and intelligent automation engines." },
  { area: "Payment Systems", desc: "PCI-compliant integrations with 5 major payment gateways and abstraction layers." },
  { area: "Mobile Development", desc: "White-label iOS & Android apps with push notifications and offline POS capabilities." },
  { area: "DevOps & Security", desc: "GDPR, HIPAA compliance with encrypted data, PII redaction, and audit logging." },
];

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Rite — Our Mission to Empower Local Businesses</title>
        <meta name="description" content="Rite Marketing Automation is built to give restaurants and healthcare providers complete ownership of their customers, revenue, and brand — eliminating marketplace dependency." />
        <meta name="keywords" content="about rite, marketing automation company, restaurant technology, healthcare SaaS, business ownership" />
        <link rel="canonical" href="https://riteclinic.com/about" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Rite Marketing Automation",
          "description": "Zero-commission SaaS platform for restaurants and healthcare providers.",
          "url": "https://riteclinic.com",
          "foundingDate": "2023",
          "industry": "SaaS, Marketing Automation"
        })}</script>
      </Helmet>

      {/* ===== HERO ===== */}
      <Box sx={{
        background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
        color: "#fff", py: { xs: 10, md: 14 },
      }}>
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Chip label="Our Story" sx={{
            bgcolor: "rgba(255,255,255,0.15)", color: "#fff", fontWeight: 600,
            mb: 3, fontSize: "0.85rem",
          }} />
          <Typography variant="h1" sx={{
            fontSize: { xs: "2rem", md: "3rem" }, fontWeight: 800, mb: 3, lineHeight: 1.2,
          }}>
            We're Building the Future of{" "}
            <Box component="span" sx={{ color: accentLight }}>Local Business Ownership</Box>
          </Typography>
          <Typography sx={{
            color: "rgba(255,255,255,0.8)", fontSize: "1.1rem", lineHeight: 1.7, maxWidth: 620, mx: "auto",
          }}>
            Rite was born from a simple observation: local businesses are paying 15–30% of
            their revenue to platforms that own the customer relationship. We believe that's
            backwards. Your customers should be yours.
          </Typography>
        </Container>
      </Box>

      {/* ===== MISSION & VISION ===== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#fff" }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 4, borderRadius: 3, border: `2px solid ${accent}`,
                height: "100%",
              }}>
                <Lightbulb sx={{ fontSize: 40, color: accent, mb: 2 }} />
                <Typography variant="h3" sx={{ fontWeight: 700, color: accent, mb: 2, fontSize: "1.5rem" }}>
                  Our Mission
                </Typography>
                <Typography sx={{ color: "#555", lineHeight: 1.8 }}>
                  To give every restaurant and healthcare provider a fully branded, zero-commission
                  platform that handles ordering, booking, marketing, loyalty, voice, and analytics
                  — so they can focus on what they do best: serving customers and patients.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 4, borderRadius: 3, bgcolor: accentLight,
                height: "100%",
              }}>
                <Rocket sx={{ fontSize: 40, color: accent, mb: 2 }} />
                <Typography variant="h3" sx={{ fontWeight: 700, color: accent, mb: 2, fontSize: "1.5rem" }}>
                  Our Vision
                </Typography>
                <Typography sx={{ color: "#555", lineHeight: 1.8 }}>
                  A world where local businesses compete on equal footing with global platforms —
                  powered by AI, owned data, and direct customer relationships. Starting with
                  restaurants and healthcare, expanding to retail, salons, gyms, and every
                  service industry.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== VALUES ===== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#fafafa" }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" sx={{
            fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 800, color: accent, mb: 2,
          }}>
            What Drives Us
          </Typography>
          <Typography textAlign="center" sx={{ color: "#666", maxWidth: 550, mx: "auto", mb: 6 }}>
            These core values shape every feature we build and every decision we make.
          </Typography>
          <Grid container spacing={3}>
            {values.map((v, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card elevation={0} sx={{
                  border: "1px solid #e0e0e0", borderRadius: 3, p: 3, height: "100%",
                  transition: "all 0.3s",
                  "&:hover": { borderColor: accent, bgcolor: accentLight },
                }}>
                  <CardContent sx={{ p: 0 }}>
                    <Avatar sx={{ bgcolor: accentLight, color: accent, mb: 2, width: 48, height: 48 }}>
                      {v.icon}
                    </Avatar>
                    <Typography sx={{ fontWeight: 700, color: "#222", mb: 1, fontSize: "1.05rem" }}>
                      {v.title}
                    </Typography>
                    <Typography sx={{ color: "#666", lineHeight: 1.6, fontSize: "0.9rem" }}>
                      {v.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== JOURNEY / MILESTONES ===== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#fff" }}>
        <Container maxWidth="md">
          <Typography variant="h2" textAlign="center" sx={{
            fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 800, color: accent, mb: 6,
          }}>
            Our Journey
          </Typography>
          <Stack spacing={0}>
            {milestones.map((m, i) => (
              <Box key={i} sx={{ display: "flex", gap: 3, pb: 4, position: "relative" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 60 }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: "50%", bgcolor: accent,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontWeight: 700, fontSize: "0.75rem", flexShrink: 0,
                  }}>
                    {m.year}
                  </Box>
                  {i < milestones.length - 1 && (
                    <Box sx={{ width: 2, flexGrow: 1, bgcolor: accentLight, mt: 1 }} />
                  )}
                </Box>
                <Box sx={{ pt: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, color: "#222", mb: 0.5 }}>{m.title}</Typography>
                  <Typography sx={{ color: "#666", fontSize: "0.9rem", lineHeight: 1.6 }}>{m.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ===== TEAM EXPERTISE ===== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#fafafa" }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" sx={{
            fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 800, color: accent, mb: 2,
          }}>
            Our Expertise
          </Typography>
          <Typography textAlign="center" sx={{ color: "#666", maxWidth: 550, mx: "auto", mb: 6 }}>
            A focused team of 3 — PM, Frontend Dev, and Backend Dev — with deep expertise across the full stack.
          </Typography>
          <Grid container spacing={3}>
            {teamExpertise.map((t, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box sx={{
                  p: 3, borderRadius: 2, bgcolor: "#fff", border: "1px solid #e0e0e0",
                  height: "100%", transition: "all 0.3s",
                  "&:hover": { borderColor: accent },
                }}>
                  <Verified sx={{ color: accent, mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, color: "#222", mb: 1 }}>{t.area}</Typography>
                  <Typography sx={{ color: "#666", fontSize: "0.88rem", lineHeight: 1.6 }}>{t.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== PLATFORM NUMBERS ===== */}
      <Box sx={{
        py: { xs: 8, md: 10 },
        background: `linear-gradient(135deg, ${accent}, ${accentDark})`,
        color: "#fff",
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} textAlign="center">
            {[
              { val: "16", label: "Modules" },
              { val: "74", label: "Features" },
              { val: "2", label: "Industries (and growing)" },
              { val: "3", label: "Delivery Phases" },
            ].map((s, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Typography sx={{ fontSize: { xs: "2.2rem", md: "3rem" }, fontWeight: 800 }}>{s.val}</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>{s.label}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== CTA ===== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: "#fff", textAlign: "center" }}>
        <Container maxWidth="sm">
          <Typography variant="h3" sx={{ fontWeight: 800, color: accent, mb: 2, fontSize: { xs: "1.6rem", md: "2rem" } }}>
            Want to Learn More?
          </Typography>
          <Typography sx={{ color: "#666", mb: 4, lineHeight: 1.7 }}>
            Explore our full suite of features or get started with a free trial today.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
            <Button variant="contained" component={Link} to="/services" endIcon={<ArrowForward />}
              sx={{
                bgcolor: accent, px: 4, py: 1.3, borderRadius: 2, fontWeight: 700,
                "&:hover": { bgcolor: accentDark },
              }}
            >
              Explore Services
            </Button>
            <Button variant="outlined" component={Link} to="/client-signup"
              sx={{
                borderColor: accent, color: accent, px: 4, py: 1.3, borderRadius: 2,
                fontWeight: 600, "&:hover": { bgcolor: accentLight },
              }}
            >
              Get Started
            </Button>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default AboutPage;
